import { useEffect, useRef } from 'react';
import { ArrowDown } from 'lucide-react';
import { gsap } from '../lib/gsapSetup';
import { HERO } from '../content/siteContent';
import { useReducedMotion } from '../hooks/useMotionPrefs';
import HeroScene from '../components/HeroScene';

export default function Hero() {
  const ref = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;
    const ctx = gsap.context(() => {
      // Opening scene — timed like a film title sequence
      const intro = gsap.timeline({ defaults: { ease: 'power3.out' } });
      intro
        .fromTo('.hero-video', { scale: 1.22 }, { scale: 1, duration: 2.6, ease: 'power2.out' }, 0.4)
        .fromTo(
          '.hero-line-inner',
          { yPercent: 120 },
          { yPercent: 0, duration: 1.15, stagger: 0.14, ease: 'power4.out' },
          0.7
        )
        .fromTo('.hero-sub', { y: 50, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9 }, 1.2)
        .fromTo('.hero-meta', { opacity: 0 }, { opacity: 1, duration: 1 }, 1.6);

      // Scroll transformation — scene one becomes scene two
      const scroll = gsap.timeline({
        scrollTrigger: {
          trigger: ref.current,
          start: 'top top',
          end: '+=110%',
          scrub: 0.5,
          pin: true,
          anticipatePin: 1,
        },
      });
      scroll
        .to('.hero-video', { scale: 1.06, ease: 'none', duration: 0.25 }, 0)
        .to('.hero-content', { yPercent: -22, opacity: 0, ease: 'power1.in', duration: 0.35 }, 0.08)
        .to('.hero-scroll-hint', { opacity: 0, duration: 0.15 }, 0.1)
        .to('.hero-frame', { scale: 0.52, borderRadius: 20, ease: 'power1.inOut', duration: 0.45 }, 0.38)
        .to('.hero-frame', { opacity: 0, scale: 0.4, ease: 'power1.in', duration: 0.2 }, 0.82);
    }, ref);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <section
      ref={ref}
      id="opening"
      data-testid="hero-section"
      className="relative h-screen overflow-hidden bg-[#000000]"
    >
      <div className="hero-frame absolute inset-0 overflow-hidden will-change-transform">
        <img
          src="/posters/hero.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <HeroScene />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
      </div>

      <div className="hero-content absolute inset-0 flex flex-col justify-end px-5 pb-10 md:px-12 md:pb-12">
        <div className="hero-meta absolute right-5 top-24 hidden text-right text-[11px] uppercase tracking-[0.25em] text-white/50 md:right-12 md:block">
          {HERO.meta}
          <span className="mt-2 block h-px w-full bg-white/20" />
        </div>

        <h1
          className="hero-headline font-display font-bold leading-[0.92] tracking-[-0.02em] text-white"
          style={{ fontSize: 'clamp(2.5rem, 9vw, 7.5rem)' }}
        >
          <span className="block overflow-hidden pb-1">
            <span className="hero-line-inner block will-change-transform">{HERO.lines[0]}</span>
          </span>
          <span className="block overflow-hidden pb-1">
            <span className="hero-line-inner flex items-center gap-4 will-change-transform md:gap-8">
              <span className="hidden h-[0.09em] w-[1.2em] bg-[#47a0d9] md:block" />
              {HERO.lines[1]}
            </span>
          </span>
          <span className="block overflow-hidden pb-2">
            <span className="hero-line-inner block will-change-transform">
              {HERO.lines[2].replace('.', '')}
              <span className="text-[#47a0d9]">.</span>
            </span>
          </span>
        </h1>

        <div className="mt-6 flex flex-col gap-6 md:mt-8">
          <p className="hero-sub max-w-md text-sm leading-relaxed text-white/70 md:text-base">
            {HERO.sub}
          </p>
        </div>
      </div>

      <div className="hero-scroll-hint absolute bottom-10 right-5 hidden items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/45 md:right-12 md:flex">
        Scroll to begin
        <ArrowDown size={13} className="animate-bounce" />
      </div>
    </section>
  );
}
